import { useState, type ReactNode } from "react";
import { TailorButton as PostingTailorButton } from "@/components/site/PostingActions";
import { trackControl } from "@/lib/analytics";
import type { Posting } from "@/lib/api/catalogue";
import type { ResumeCheck } from "@/lib/hooks/resumeCheck";
import { useDesktop } from "@/lib/hooks/viewport";
import type { ToolJob } from "@/lib/tools/pastedJob";
import type { KeptJob } from "@/lib/tools/tailorAnswer";
import CheckHeading from "./CheckHeading";
import CheckPanel from "./CheckPanel";
import JobBox from "./JobBox";
import PostingBox from "./PostingBox";
import ResultStrip from "./ResultStrip";
import ResumeBox from "./ResumeBox";
import TailorForm from "./TailorForm";
import { TailorButton, type TailorSlot } from "./TailorBlock";

const COLUMN_GAP = "gap-5 max-[760px]:gap-3.5";

/** The line over the resume box, for what the panel beside it shows. */
function resumeCaption(settled: boolean, skills: number, closed: boolean, member: boolean): string {
    // A member's tailor reads their saved resume, so this box only feeds the check.
    if (member) return "This box is only for this check. Tailor uses your saved resume.";
    if (!settled) return "Paste it, or pick a file.";
    if (skills > 0) return "Edit your resume here. The count updates as you type.";
    return closed ? "Stays in this browser." : "Stays in this browser until you press Tailor.";
}

/**
 * The Tailor button for each place. A closed job takes no application, so
 * nothing there offers to tailor for it. One tailored says so, and sends
 * nothing more: its resume opens from the status above.
 */
function tailorSlot(
    posting: Posting | null,
    resume: string,
    press: {
        busy: boolean;
        done: boolean;
        /** Opens the form that asks which job a pasted one is. */
        openForm: () => void;
        send: (job: ToolJob) => void;
    },
): TailorSlot {
    const { busy, done, openForm, send } = press;
    if (posting?.closed_at) return null;
    if (!posting) return (place) => <TailorButton onTailor={openForm} place={place} busy={busy} />;
    // The funnel counts a press here as it counts the form's, beside the button's own event.
    return (place) => (
        <span
            className="contents"
            onClickCapture={() =>
                trackControl("tool_tailor_pressed", { source: "ResumeCheck", section: place })
            }
        >
            <PostingTailorButton
                postingId={posting.posting_id}
                job={{ title: posting.title, company: posting.company }}
                queued={busy}
                tailored={done}
                size={place === "panel" ? "tall" : "md"}
                onTailor={() => send({ postingId: posting.posting_id, resume })}
            />
        </span>
    );
}

/**
 * The tool, for a pasted job or a posting: the inputs, and the result
 * beside them. On a phone it is one column, and a result adds a strip
 * under the header with the count and Tailor. Tailor on a posting looks
 * like the posting page's own button and gives `onTailor` the posting at
 * once. On a pasted job it opens the form that asks which job it is, and
 * `onTailor` is given that job once it is answered. `status` is where that
 * tailor stands, at the head of the panel.
 */
export default function CheckPage({
    tool,
    posting,
    visitor,
    member,
    onTailor,
    tailoring,
    tailored,
    status,
    keptJob,
}: {
    tool: ResumeCheck;
    posting: Posting | null;
    /** Signed out: sees the allowance. */
    visitor: boolean;
    /** Signed in: tailors from the saved resume. */
    member: boolean;
    onTailor: (job: ToolJob) => void;
    /** A tailor is under way: Tailor is not offered again until it ends. */
    tailoring: boolean;
    /** The tailor has written its resume. */
    tailored: boolean;
    status: ReactNode;
    /** A job back for the form. One with a problem opens it, with the problem on the link. */
    keptJob: KeptJob | null;
}) {
    const [editing, setEditing] = useState(false);
    // Open on a result, or on a refused link, which can come with no result: see below.
    const [asking, setAsking] = useState<false | "result" | "refusal">(false);
    const [shownJob, setShownJob] = useState<KeptJob | null>(null);
    const desktop = useDesktop();
    const { fit, keywords } = tool;
    // A posting that names no skill has nothing to check, so a resume is all it waits for.
    const settled = keywords !== null || (fit?.asked === 0 && tool.resume.trim() !== "");
    // The form belongs to the result it opened on: an edited job takes the result, and the form with it.
    if (asking === "result" && !settled) setAsking(false);
    // Back from sign-in the boxes are restored with no result yet, and the link still needs fixing.
    if (keptJob !== shownJob) {
        setShownJob(keptJob);
        if (keptJob?.problem) setAsking(settled ? "result" : "refusal");
    }
    const tailor = tailorSlot(posting, tool.resume, {
        busy: tailoring,
        done: tailored,
        openForm: () => setAsking("result"),
        send: onTailor,
    });
    return (
        <div>
            {settled && fit && <ResultStrip fit={fit} tailor={tailor} />}
            <CheckHeading posting={posting} settled={settled} />
            <div
                data-testid="check-grid"
                className={`grid grid-cols-1 items-start min-[1024px]:grid-cols-[minmax(0,1fr)_460px] ${COLUMN_GAP}`}
            >
                <div data-testid="check-inputs" className={`flex min-w-0 flex-col ${COLUMN_GAP}`}>
                    {posting ? (
                        <PostingBox posting={posting} />
                    ) : (
                        <JobBox
                            job={tool.job}
                            onChange={tool.setJob}
                            found={keywords && !editing ? keywords.length : null}
                            onEdit={() => setEditing(true)}
                            editing={editing}
                        />
                    )}
                    <ResumeBox
                        resume={tool.resume}
                        onChange={tool.setResume}
                        onPick={(file) => void tool.pickFile(file)}
                        caption={
                            tool.readingFile
                                ? "Reading your file."
                                : resumeCaption(settled, fit?.asked ?? 0, tailor === null, member)
                        }
                        settled={settled}
                    />
                </div>
                {asking && desktop ? null : (
                    <CheckPanel
                        fit={fit}
                        keywords={keywords}
                        settled={settled}
                        waiting={posting ? (posting.keywords ?? []) : null}
                        problem={tool.problem}
                        checking={tool.checking}
                        visitor={visitor}
                        member={member}
                        status={status}
                        onCheck={() => {
                            setEditing(false);
                            tool.check();
                        }}
                        tailor={tailor}
                    />
                )}
                {!posting && (
                    <TailorForm
                        open={asking !== false}
                        fit={fit}
                        description={tool.job}
                        resume={tool.resume}
                        member={member}
                        kept={keptJob}
                        onSubmit={(job) => {
                            // A press that lands as the form closes sends nothing more.
                            if (!asking) return;
                            setAsking(false);
                            onTailor(job);
                        }}
                        onClose={() => setAsking(false)}
                    />
                )}
            </div>
        </div>
    );
}
